import React, { useEffect, useState, useMemo, useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import usePulidoLink from "../../hooks/usePulidoLink";
import { formatItemSubId } from "../../utils";
import ExpandableTable from "../../components/design-system/ExpandableTable";
import StatusBadge from "../../components/design-system/StatusBadge";
import ConfirmModal from "../../components/design-system/ConfirmModal";
import PieceLookup from "../../components/design-system/PieceLookup";
import DocumentAttacher from "../../components/design-system/DocumentAttacher";
import RespaldoMaterial from "./RespaldoMaterial";
import WorkOrderProcessesPanel from "./WorkOrderProcessesPanel";
import CostosOT from "./CostosOT";
import FacturarModal from "../../components/design-system/FacturarModal";

// F5.9 / D-T33 — Tabs para los paneles operativos del detalle expandido.
// D-WO-Fase1 — Tab Documentos ahora desglosa por pieza (PI) para subir docs
// específicos de cada fabricación (control dimensional firmado, foto producto
// terminado, certificado material del lote). El uploader general de la WO
// queda arriba para docs genéricos (guía, factura, OC original).
// D-Stock-Gestion — Box para cerrar OT interna (fabricación a stock).
// Sin factura/guía: solo confirma fabricación → status delivered + suma al stock.
const CerrarInternoBox = ({ workOrderId, pieces, onClosed }) => {
  const [closing, setClosing] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null); // { stockUpdates, stockMovimientos }

  const totalQty = (pieces || []).reduce((s, p) => s + (Number(p.quantity) || 0), 0);

  const cerrar = async () => {
    if (!window.confirm(`¿Confirmar cierre? Se sumará al stock las cantidades fabricadas (${totalQty} unidades).`)) return;
    setClosing(true); setErr(null);
    try {
      const res = await backend.patch(`workOrder/${workOrderId}/cerrar-interno`);
      setResult({
        stockUpdates:    res.data?.stockUpdates    || [],
        stockMovimientos: res.data?.stockMovimientos || [],
      });
      onClosed?.();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    }
    setClosing(false);
  };

  if (result) return (
    <div style={{
      marginTop: 14, padding: "10px 14px",
      background: "#0d2a15", border: "1px solid #0d4020", borderRadius: 6,
      fontSize: 12, color: "#2ecc71",
    }}>
      ✓ OT cerrada.
      {result.stockUpdates.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: "#2ecc71", fontWeight: 600 }}>📦 Piezas ingresadas a stock:</div>
          {result.stockUpdates.map(u => (
            <div key={u.pieceId} style={{ paddingLeft: 14, color: "#aaa", fontSize: 11 }}>
              • {u.sku || u.name}: +{u.added} → ahora {u.newStock} en stock
            </div>
          ))}
        </div>
      )}
      {result.stockMovimientos.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: "#e67e22", fontWeight: 600 }}>🪵 Material consumido:</div>
          {result.stockMovimientos.map((m, i) => (
            <div key={i} style={{ paddingLeft: 14, color: "#aaa", fontSize: 11 }}>
              • {m.materialName}: −{m.qty} {m.unit}
            </div>
          ))}
        </div>
      )}
      {result.stockUpdates.length === 0 && result.stockMovimientos.length === 0 && (
        <div style={{ color: "#cbd5e1", marginTop: 4 }}>Ninguna pieza tenía gestión de stock o material vinculado.</div>
      )}
    </div>
  );

  return (
    <div style={{
      marginTop: 14, padding: "10px 14px",
      background: "#1a1500", border: "1px solid #4a3500", borderRadius: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" /* móvil */ }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#f39c12", marginBottom: 2 }}>
            🔧 OT interna para stock
          </div>
          <div style={{ fontSize: 11, color: "#cbd5e1" }}>
            Al cerrar, las {totalQty} unidades fabricadas se suman al stock de las piezas (managed). Sin factura.
          </div>
        </div>
        <button onClick={cerrar} disabled={closing} style={{
          background: "#f39c12", color: "#1a1500",
          border: "none", borderRadius: 5,
          padding: "7px 14px", fontSize: 12, fontWeight: 700,
          cursor: "pointer", opacity: closing ? 0.6 : 1,
          whiteSpace: "nowrap",
        }}>{closing ? "Cerrando…" : "✓ Cerrar e ingresar a stock"}</button>
      </div>
      {err && <div style={{ marginTop: 6, color: "#e74c3c", fontSize: 11 }}>{err}</div>}
    </div>
  );
};

const ActivePanelsTabs = ({ workOrderId, verCostos, quotationId }) => {
  const [tab, setTab] = useState("procesos");
  const tabs = [
    { key: "procesos",   label: "Procesos por pieza" },
    { key: "documentos", label: "Documentos" },
    // Costeo por OT (27-ago): compras + rendiciones vinculadas vs venta.
    // 28-ago (auditoría roles): solo admin/accountant — al resto el endpoint
    // le responde 403 y el tab quedaba vacío, así que no se ofrece.
    ...(verCostos ? [{ key: "costos", label: "💰 Costos" }] : []),
  ];
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        display: "flex", gap: 4, borderBottom: "1px solid #2e2e2e",
        marginBottom: 12,
      }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: "7px 14px",
                background: active ? "#1e1e1e" : "transparent",
                border: "none",
                borderBottom: active ? "2px solid #3498db" : "2px solid transparent",
                color: active ? "#fff" : "#888",
                fontSize: 11,
                fontWeight: active ? 700 : 500,
                textTransform: "uppercase",
                letterSpacing: ".05em",
                cursor: "pointer",
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {tab === "procesos" && <WorkOrderProcessesPanel workOrderId={workOrderId} />}
      {/* F1 — el respaldo de material que dejó ventas. Va arriba de los documentos de la
          OT porque es lo que hay que mirar ANTES de fabricar, no después. */}
      {tab === "documentos" && <RespaldoMaterial quotationId={quotationId} workOrderId={workOrderId} />}
      {tab === "documentos" && <WorkOrderDocumentsPanel workOrderId={workOrderId} />}
      {tab === "costos" && <CostosOT workOrderId={workOrderId} />}
    </div>
  );
};

// D-WO-Fase1 — Panel de documentos de la WO desglosado:
//   1) Documentos generales de la OT (workOrderRefs): guía, factura, OC del cliente.
//   2) Por cada pieza fabricada (PI): docs específicos de esa fabricación
//      (control dimensional firmado, foto producto, certificado material lote).
const WorkOrderDocumentsPanel = ({ workOrderId }) => {
  const [pis, setPis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (!workOrderId) return;
    setLoading(true);
    backend.get(`pieceInstance?workOrder=${workOrderId}`)
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data.items || data.pieceInstances || []);
        setPis(items);
      })
      .catch(() => setPis([]))
      .finally(() => setLoading(false));
  }, [workOrderId]);

  const toggleExpand = id => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div>
      {/* 1) Documentos generales de la OT */}
      <div style={{
        background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6,
        padding: "10px 14px", marginBottom: 12,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: "#cbd5e1",
          textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8,
        }}>
          Documentos generales de la OT
          <span style={{ marginLeft: 8, fontWeight: 400, color: "#999", textTransform: "none" }}>
            (guía, factura, OC del cliente, etc.)
          </span>
        </div>
        <DocumentAttacher
          refField="workOrderRefs"
          refId={workOrderId}
          defaultType="otro"
        />
      </div>

      {/* 2) Por cada pieza de la OT — docs específicos de esa fabricación */}
      <div style={{
        fontSize: 10, fontWeight: 700, color: "#cbd5e1",
        textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8,
      }}>
        Documentos por pieza fabricada
        <span style={{ marginLeft: 8, fontWeight: 400, color: "#999", textTransform: "none" }}>
          (control dimensional firmado, foto producto terminado, cert. material del lote)
        </span>
      </div>

      {loading && (
        <div style={{ padding: 12, color: "#aaa", fontSize: 11 }}>Cargando piezas…</div>
      )}
      {!loading && pis.length === 0 && (
        <div style={{ padding: 12, color: "#aaa", fontSize: 11, fontStyle: "italic" }}>
          Sin piezas asociadas a esta OT.
        </div>
      )}
      {pis.map(pi => {
        const isOpen = expanded.has(pi._id);
        const sku  = pi.skuSnapshot || pi.piece?.sku || "sin SKU";
        const name = pi.pieceNameSnapshot || pi.piece?.name || "(sin nombre)";
        // pi.piece puede venir populado (objeto) o como string id
        const pieceCatalogId = (typeof pi.piece === "object" && pi.piece !== null)
          ? (pi.piece._id || pi.piece.id)
          : pi.piece;
        return (
          <div key={pi._id} style={{
            border: "1px solid #2e2e2e", borderRadius: 6,
            marginBottom: 8, background: "#161616", overflow: "hidden",
          }}>
            <div
              onClick={() => toggleExpand(pi._id)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px", cursor: "pointer", userSelect: "none",
                background: isOpen ? "#1a1a1a" : "#161616",
                borderBottom: isOpen ? "1px dashed #2e2e2e" : "none",
              }}
            >
              <span style={{ color: "#999", fontSize: 11, width: 12 }}>{isOpen ? "▼" : "▶"}</span>
              <span style={{
                fontFamily: "monospace", fontSize: 11, color: "#3498db",
                background: "#0d1e2e", padding: "2px 8px", borderRadius: 4,
              }}>
                {sku}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#ddd" }}>
                {name}
              </span>
              {pi.quantity > 1 && (
                <span style={{ fontSize: 11, color: "#cbd5e1" }}>× {pi.quantity}</span>
              )}
            </div>
            {isOpen && (
              <div style={{ padding: "10px 14px" }}>
                {/* Catálogo: plano técnico, manuales — compartido entre todas las fabricaciones */}
                {pieceCatalogId ? (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{
                      fontSize: 9, fontWeight: 700, color: "#a78bfa",
                      textTransform: "uppercase", letterSpacing: ".06em",
                      marginBottom: 6, display: "flex", alignItems: "center", gap: 6,
                    }}>
                      📁 Del catálogo de la pieza
                      <span style={{ fontWeight: 400, color: "#aaa", textTransform: "none", letterSpacing: 0 }}>
                        — plano técnico, manuales (compartido entre todas las fabricaciones)
                      </span>
                    </div>
                    <DocumentAttacher
                      refField="pieceRef"
                      refId={pieceCatalogId}
                      defaultType="planoTecnico"
                    />
                  </div>
                ) : (
                  <div style={{
                    background: "#2d2000", border: "1px solid #4a3500",
                    color: "#f39c12", padding: "8px 12px", borderRadius: 5,
                    fontSize: 11, marginBottom: 14,
                  }}>
                    ⚠ Esta pieza no está vinculada al catálogo (es temp/custom).
                    Solo se pueden subir documentos específicos de esta fabricación.
                  </div>
                )}

                {/* Esta fabricación: control dim firmado, foto producto, cert material lote */}
                <div>
                  <div style={{
                    fontSize: 9, fontWeight: 700, color: "#3498db",
                    textTransform: "uppercase", letterSpacing: ".06em",
                    marginBottom: 6, display: "flex", alignItems: "center", gap: 6,
                  }}>
                    🔧 De esta fabricación
                    <span style={{ fontWeight: 400, color: "#aaa", textTransform: "none", letterSpacing: 0 }}>
                      — control dimensional firmado, foto producto, certificado material del lote
                    </span>
                  </div>
                  <DocumentAttacher
                    refField="pieceInstanceRef"
                    refId={pi._id}
                    defaultType="controlDimensional"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── constantes ──────────────────────────────────────────────────────────────

const CLOSED_STATUSES = ["delivered", "facturado"];
const TERMINAL_STATUSES = ["delivered", "facturado", "cancelled"];

// D-WO-Fase1 — Cards top reemplazan los tabs antiguos.
// "En taller con OC" coincide 1:1 con /produccion "OCs activas"
// (alineación conceptual: ambas reflejan trabajo activo con OC formal).
// "Manual del taller" expone los trabajos del taller sin OC del cliente.
const STATUS_CARDS = [
  { value: "all",              label: "Total",              color: "#3498db" },
  { value: "cotizado",         label: "Cotizadas",          color: "#a78bfa",
    title: "WOs espejo de cotizaciones esperando aprobación del cliente" },
  { value: "en_taller_con_oc", label: "En taller (con OC)", color: "#f39c12",
    title: "Trabajo activo con OC formal del cliente. Coincide con /produccion → OCs activas" },
  { value: "manual_taller",    label: "Manual del taller",  color: "#e67e22",
    title: "Trabajo activo del taller sin OC formal (verbal o sin cotización)" },
  { value: "delivered",        label: "Entregadas",         color: "#2ecc71",
    title: "Cerradas con guía de despacho" },
  { value: "facturado",        label: "Facturadas",         color: "#16a085",
    title: "Cerradas con factura" },
  { value: "cancelled",        label: "Canceladas",         color: "#e74c3c" },
];

const STATUS_OPTIONS = [
  { value: "cotizado",  label: "Cotizada"    },
  { value: "working",   label: "En proceso"  },
  { value: "ready",     label: "Terminado"   },
  { value: "delivered", label: "Entregado"   },
  { value: "facturado", label: "Facturada"   },
  { value: "cancelled", label: "Cancelada"   },
];

// F5.8 — Chips para filtrar por origen (ortogonal a status).
// Tres categorías de cómo entró el trabajo al taller:
//   - oc_cliente:  vino con OC formal del cliente (origin OC o customerOrderRef)
//   - cotizacion:  vino de cotización aprobada sin OC (origin COT o quotationRef)
//   - manual:      trabajo del taller sin cotización ni OC (origin sinOC/verbal)
const ORIGIN_CHIPS = [
  { value: "all",         label: "Todos los orígenes", color: "#ccc"    },
  { value: "oc_cliente",  label: "Desde OC cliente",   color: "#3498db" },
  { value: "cotizacion",  label: "Desde cotización",   color: "#a78bfa" },
  { value: "manual",      label: "Manual del taller",  color: "#f39c12" },
  { value: "interno",     label: "Interno Binimeliz",  color: "#e67e22" },
];

// F5.8 — Clasificador de origen. Combina campo `origin` con refs (más robusto
// que solo mirar customerOrderRef, especialmente para WOs viejas con `origin:'OC'`
// pero sin la ref nueva del schema F4.11).
const classifyOrigin = wo => {
  if (wo.customerOrderRef) return "oc_cliente";
  if (wo.origin === "OC")  return "oc_cliente";
  if (wo.quotationRef)     return "cotizacion";
  if (wo.origin === "COT") return "cotizacion";
  if (wo.origin === "interno") return "interno";
  if (["verbal", "sinOC"].includes(wo.origin)) return "manual";
  return "manual"; // fallback (incluye orphan/null) → manual del taller
};

const ORIGIN_COLOR = {
  OC:     "#3498db",
  COT:    "#a78bfa",
  verbal: "#aaa",
  sinOC:  "#666",
  orphan: "#e74c3c",
};

const ORIGIN_LABEL = {
  OC:     "OC",
  COT:    "COT",
  verbal: "Verbal",
  sinOC:  "Sin OC",
  orphan: "Huérfana",
};

const PER_PAGE = 50;

const S = {
  wrap: {
    // clamp: 22px lateral en desktop, ~10px en móvil (360-430px)
    display: "block", padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
    minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
    fontSize: 13, textAlign: "left", boxSizing: "border-box", width: "100%",
  },
};

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt  = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "2-digit" }) : "fecha no registrada";
const fmtN = n => Number(n || 0).toLocaleString("es-CL");

const motivoSinOC = wo => {
  if (wo.quotationRef || wo.origin === "COT") return "sin OC aún (cotización)";
  if (wo.origin === "interno") return "no aplica (trabajo interno)";
  if (["verbal", "sinOC"].includes(wo.origin)) return "sin OC (trabajo directo)";
  if (wo.origin === "orphan") return "OC eliminada";
  return "sin OC registrada";
};

const clienteOT = wo => {
  if (wo.client?.name) return wo.client.name;
  return wo.origin === "interno" ? "🔧 Trabajo interno" : "cliente no registrado";
};

const normWOItems = (pieces = []) =>
  pieces.map(p => {
    const piece = p.piece && typeof p.piece === "object" ? p.piece : null;
    return {
      // F4.13 — fallback al p.sku/p.desc del WO.pieces[] cuando no hay piece populado
      sku:     piece?.sku   || p.sku  || "",
      desc:    piece?.name  || p.desc || "",
      qty:     p.quantity   || 1,
      pUnit:   p.price      || piece?.price || 0,
      source:  p.source || (piece ? "catalog" : null),
      pieceId: piece?._id   || null,
    };
  });

// ─── componente principal ─────────────────────────────────────────────────────

const WorkOrders = () => {
  useTitle("Trabajos / OTs");
  const navigate = useNavigate();
  const location = useLocation();
  const [urlParams] = useSearchParams();

  // 28-ago (auditoría roles) — permisos por rol EFECTIVO (respeta vistaComo):
  //   puedeOperar:    crear/editar/eliminar OTs + emitir factura/guía (admin/operator).
  //   puedeVerCostos: tab 💰 Costos (endpoint solo admin/accountant).
  //   esAdmin:        links a /pulido (módulo solo-admin).
  // El seller queda en modo consulta: el backend igual rechaza, esto evita
  // ofrecerle botones que solo terminan en 403.
  const auth = useContext(AuthContext);
  const userRoles = auth?.user?.roles || [];
  const puedeOperar    = ["admin", "operator"].some(r => userRoles.includes(r));
  const puedeVerCostos = auth?.can?.("ver_costos") || false;
  const puedeVerPulido = auth?.can?.("ver_pulido") || false;

  const [workOrders,   setWorkOrders]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [clients,      setClients]      = useState([]);
  const [activeTab,    setActiveTab]    = useState("all");
  const [originFilter, setOriginFilter] = useState("all"); // F5.3
  // ?ot=N en la URL (p. ej. desde /emitir?ot=N sin tipo) prefiltra la lista por ese número.
  const [searchTerm,   setSearchTerm]   = useState(() => (/^\d+$/.test(urlParams.get("ot") || "") ? urlParams.get("ot") : ""));
  const [clientFilter, setClientFilter] = useState("");
  const [page,         setPage]         = useState(1);
  const [detailsModal, setDetailsModal] = useState(null);
  // Fase G (26-ago): modal de emisión real vía SimpleFactura
  const [facturarTarget, setFacturarTarget] = useState(null); // { woNumber, nombre } → FacturarModal
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── data ──────────────────────────────────────────────────────────────────

  const load = async () => {
    setLoading(true);
    try {
      const [woRes, clRes] = await Promise.all([
        backend.get("workOrder"),
        backend.get("entity").catch(() => ({ data: [] })),
      ]);
      setWorkOrders(
        (woRes.data || []).map(wo => ({
          ...wo,
          items: normWOItems(wo.pieces || []),
        }))
      );
      setClients(clRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (location.state?.new) {
      const wo = location.state.new;
      setWorkOrders(p => [{ ...wo, items: normWOItems(wo.pieces || []) }, ...p]);
      // Resetear filtros para garantizar que la OT recién creada sea visible.
      // Antes podía caer en una tab que la usuaria no estaba viendo (ej:
      // "en_taller_con_oc" excluye OTs sinOC sin clientOC explícito).
      setActiveTab("all");
      setOriginFilter("all");
      setSearchTerm("");
      setClientFilter("");
      setPage(1);
      navigate("", { replace: true });
    }
    if (location.state?.edit) {
      const wo = location.state.edit;
      setWorkOrders(p => p.map(v =>
        v._id === wo._id ? { ...wo, items: normWOItems(wo.pieces || []) } : v
      ));
      navigate("", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // ── filtrado + paginación ─────────────────────────────────────────────────

  // F5.9 — Una WO está "en producción" si:
  //   Caso A: tiene CO con closedAt null  (CO activa)
  //   Caso B: sin CO ni quotation (sinOC/manual/interno) y no terminal
  //   Excluye: cotizaciones no aprobadas (quotationRef.status === "cotizada")
  // Misma definición que /produccion → conteos alineados.
  const isInProduction = (wo) => {
    if (TERMINAL_STATUSES.includes(wo.status)) return false;
    // Cotización no aprobada → vive en /quotations, no es activa.
    // Ago-27: el funnel de ventas suma estados pre-aprobación (enviada/
    // seguimiento) y de cierre comercial sin OT (perdida/vencida).
    const QUOT_NO_APROBADA = ["cotizada", "enviada", "seguimiento", "perdida", "vencida", "open", "sent", "revision"];
    if (wo.quotationRef && QUOT_NO_APROBADA.includes(wo.quotationRef.status)) return false;
    // Caso A: CO activa
    if (wo.customerOrderRef) {
      return !wo.customerOrderRef.closedAt;
    }
    // Caso B: sin CO ni quotation, o Caso C: con quotation aprobada/terminada-no-cerrada
    return true;
  };
  const hasOC = (wo) => !!(wo.customerOrderRef || (wo.clientOC && String(wo.clientOC).trim()));

  const filtered = useMemo(() => {
    let rows = workOrders;
    // D-WO-Fase1 — cards desglosan en proceso por origen para alinear con /produccion.
    if (activeTab === "en_taller_con_oc") {
      rows = rows.filter(wo => isInProduction(wo) && hasOC(wo));
    } else if (activeTab === "manual_taller") {
      rows = rows.filter(wo => isInProduction(wo) && !hasOC(wo) && !wo.quotationRef);
    } else if (activeTab === "activas") {
      // backward compat — chip activas legacy puede llegar via URL
      rows = rows.filter(isInProduction);
    } else if (activeTab === "cerradas") {
      rows = rows.filter(wo => CLOSED_STATUSES.includes(wo.status));
    } else if (activeTab !== "all") {
      rows = rows.filter(wo => wo.status === activeTab);
    }
    if (originFilter !== "all") {
      rows = rows.filter(wo => classifyOrigin(wo) === originFilter);
    }
    if (clientFilter) rows = rows.filter(wo => (wo.client?._id || wo.client) === clientFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      rows = rows.filter(wo =>
        String(wo.number || "").includes(q) ||
        (wo.clientOC  || "").toLowerCase().includes(q) ||
        (wo.name      || "").toLowerCase().includes(q) ||
        (wo.description || "").toLowerCase().includes(q) ||
        (wo.client?.name || "").toLowerCase().includes(q) ||
        (wo.items || []).some(it =>
          (it.sku  || "").toLowerCase().includes(q) ||
          (it.desc || "").toLowerCase().includes(q)
        )
      );
    }
    return rows;
  }, [workOrders, activeTab, originFilter, clientFilter, searchTerm]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE) || 1;

  const pageRows = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, page]);

  useEffect(() => { setPage(1); }, [activeTab, originFilter, searchTerm, clientFilter]);

  const tabCounts = useMemo(() => {
    const counts = { all: workOrders.length };
    counts.activas  = workOrders.filter(isInProduction).length; // backward compat
    counts.cerradas = workOrders.filter(wo => CLOSED_STATUSES.includes(wo.status)).length;
    counts.en_taller_con_oc = workOrders.filter(wo => isInProduction(wo) && hasOC(wo)).length;
    counts.manual_taller    = workOrders.filter(wo => isInProduction(wo) && !hasOC(wo) && !wo.quotationRef).length;
    STATUS_CARDS.forEach(c => {
      if (["all", "en_taller_con_oc", "manual_taller"].includes(c.value)) return;
      counts[c.value] = workOrders.filter(wo => wo.status === c.value).length;
    });
    return counts;
  }, [workOrders]);

  // F5.8 — Conteos por origen (sobre el conjunto filtrado por status, para que
  // los chips reflejen lo que se está viendo en la tabla actual)
  const originCounts = useMemo(() => {
    let baseRows = workOrders;
    if (activeTab === "en_taller_con_oc") {
      baseRows = baseRows.filter(wo => isInProduction(wo) && hasOC(wo));
    } else if (activeTab === "manual_taller") {
      baseRows = baseRows.filter(wo => isInProduction(wo) && !hasOC(wo) && !wo.quotationRef);
    } else if (activeTab === "activas") {
      baseRows = baseRows.filter(isInProduction);
    } else if (activeTab === "cerradas") {
      baseRows = baseRows.filter(wo => CLOSED_STATUSES.includes(wo.status));
    } else if (activeTab !== "all") {
      baseRows = baseRows.filter(wo => wo.status === activeTab);
    }
    const c = { all: baseRows.length, oc_cliente: 0, cotizacion: 0, manual: 0, interno: 0 };
    for (const wo of baseRows) c[classifyOrigin(wo)]++;
    return c;
  }, [workOrders, activeTab]);

  // D-T51b — Link directo desde /pulido (?openId=<woId>&editField=<campo>)
  const pulidoLink = usePulidoLink({
    rows: workOrders,
    getNumber: r => r.number,
  });

  // D-T51b — Si llegamos con openId pero la WO está fuera del filtro/página actual,
  // reset filters + jump a la página que la contiene para que el expand sea visible.
  useEffect(() => {
    if (!pulidoLink.openId || !workOrders.length) return;
    const target = workOrders.find(w => w._id === pulidoLink.openId);
    if (!target) return;
    const inFiltered = filtered.some(w => w._id === pulidoLink.openId);
    if (!inFiltered) {
      setActiveTab("all");
      setOriginFilter("all");
      setSearchTerm("");
      setClientFilter("");
      return;
    }
    const idx = filtered.findIndex(w => w._id === pulidoLink.openId);
    const targetPage = Math.floor(idx / PER_PAGE) + 1;
    if (targetPage !== page) setPage(targetPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulidoLink.openId, workOrders, filtered]);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleSave = async (id, draft) => {
    const calcTotal = (draft.items || []).reduce(
      (s, it) => s + (it.pUnit || 0) * (it.qty || 1), 0
    );
    const payload = {
      client:       draft.client?._id || draft.client,
      status:       draft.status,
      description:  draft.description,
      clientOC:     draft.clientOC     || undefined,
      guiaDespacho: draft.guiaDespacho || undefined,
      factura:      draft.factura      || undefined,
      observations: draft.observations || undefined,
      total:        calcTotal || draft.total || 0,
      pieces: (draft.items || []).map(it => ({
        piece:    it.pieceId  || undefined,
        // Preservar sku/desc: para ítems sin catálogo (Rocmin importados, temp)
        // el pre-save del backend no puede rellenarlos → quedaban en blanco al
        // editar, y la factura salía con líneas sin descripción.
        sku:      it.sku      || undefined,
        desc:     it.desc     || it.name || undefined,
        quantity: it.qty      || 1,
        price:    it.pUnit    || 0,
      })),
    };
    await backend.patch(`workOrder/${id}`, payload);
    setWorkOrders(prev => prev.map(wo =>
      wo._id === id ? { ...wo, ...draft, total: payload.total } : wo
    ));
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      await backend.delete(`workOrder/${deleteTarget._id}`);
      setWorkOrders(prev => prev.filter(wo => wo._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch (e) {
      alert("Error al eliminar: " + (e?.response?.data?.error || e.message));
    }
  };

  // Pre-emisión (diseño Enternet): factura o guía armada por el ERP con los
  // datos de la OT. Usa el folio digitado en el campo respectivo (si está);
  // si no, el backend sugiere el siguiente del cache SII. Sale marcada
  // PRE-EMISIÓN — el DTE real lo emite Enternet/SII.
  const handlePreDte = async (tipo) => {
    if (!detailsModal?.number) return;
    const folio = tipo === "guia" ? (detailsModal.guiaDespacho || "") : (detailsModal.factura || "");
    const w = window.open("", "_blank"); // sincrónico, antes del await (iOS Safari)
    try {
      const res = await backend.get(`dte/pre/${tipo}/${detailsModal.number}`, {
        params: folio ? { folio } : {},
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (w) w.location = url; else window.location = url;
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) w.close();
      alert("No se pudo generar la pre-emisión. " + (e?.message || ""));
    }
  };

  // ── Emisión real vía SimpleFactura ─────────────────────────────────────────
  // La factura 33 sale por el FacturarModal único (fuente "ot"): carga el saldo por ítem,
  // muestra el ambiente y emite. `woSrc` permite abrirlo directo desde la barra de acciones
  // del expand o la fila (sin pasar por "Más detalles"); default = detailsModal.
  const abrirFacturar = (woSrc = detailsModal) => {
    if (!woSrc?.number) return;
    setFacturarTarget({ woNumber: woSrc.number, nombre: woSrc.name });
  };

  // 26-oct: la guía no sale por un modal de aquí. POST /dte/emitir-guia responde 410 desde
  // el 31-ago (Res. SII 154: chofer, cédula, transportista, destino, hora) y la pantalla
  // Emitir es la que pide esos datos. Se va allá con la OT y el tipo precargados.
  const abrirGuia = (woSrc = detailsModal) => {
    if (!woSrc?.number) return;
    setDetailsModal(null);
    navigate(`/emitir?ot=${woSrc.number}&tipo=guia`);
  };

  const handleSaveDetails = async () => {
    if (!detailsModal?._id) return;
    // 28-ago (auditoría): sin catch el error (403, validación) se tragaba y el
    // modal quedaba como si hubiera guardado. Ahora avisa y el modal sigue
    // abierto para reintentar o cancelar.
    try {
      await backend.patch(`workOrder/${detailsModal._id}`, {
        guiaDespacho: detailsModal.guiaDespacho || undefined,
        factura:      detailsModal.factura      || undefined,
        observations: detailsModal.observations || undefined,
      });
      setWorkOrders(prev => prev.map(wo =>
        wo._id === detailsModal._id
          ? {
              ...wo,
              guiaDespacho: detailsModal.guiaDespacho,
              factura:      detailsModal.factura,
              observations: detailsModal.observations,
            }
          : wo
      ));
      setDetailsModal(null);
    } catch (e) {
      alert("No se pudo guardar los detalles: " + (e?.response?.data?.error || e.message));
    }
  };

  // ── columnas ──────────────────────────────────────────────────────────────

  const columns = [
    {
      // F5.1 — keyField sigue siendo createdAt (para que el sort no rompa) pero
      // el render usa row.date (real, mes_origen) con fallback a createdAt
      // para WOs viejas pre-F5.1 que no tienen `date`.
      key: "createdAt", label: "Fecha", width: 85,
      render: (_, row) => (
        <span style={{ color: "#cbd5e1", fontSize: 11 }}>
          {fmt(row.date || row.createdAt)}
        </span>
      ),
    },
    {
      key: "number", label: "N° OT", width: 70,
      render: (_, row) => (
        <span style={{ fontFamily: "monospace", color: "#3498db", fontWeight: 700 }}>
          {row.number || "número no asignado"}
        </span>
      ),
    },
    {
      key: "client", label: "Cliente", width: 160,
      render: (_, row) => {
        // Una OT interna no tiene cliente: decirlo es contexto, el "—" era mudo.
        if (!row.client?.name) {
          if (row.origin === "interno") return <span style={{ color: "#f39c12", fontWeight: 700, fontSize: 11 }}>🔧 Trabajo interno</span>;
          return "cliente no registrado";
        }
        const name = row.client.name;
        return name.length > 22 ? name.substring(0, 22) + "…" : name;
      },
    },
    {
      key: "description", label: "Descripción",
      render: (_, row) => {
        // Fallback en cascada:
        //   1) row.description (set explícito en form / POST quotation)
        //   2) quotationRef.description (legacy WOs antes del fix de copia)
        //   3) items: "sku/desc1, sku/desc2 +N" — para WOs nacidas de OC
        //      donde POST /customerOrder no setea description (ítems son la
        //      única señal de qué se está fabricando).
        let desc = (row.description || row.quotationRef?.description || "").trim();
        if (!desc && Array.isArray(row.items) && row.items.length > 0) {
          const labels = row.items
            .map(it => (it.sku || it.desc || "").trim())
            .filter(Boolean);
          if (labels.length > 0) {
            const head = labels.slice(0, 2).join(", ");
            const more = labels.length > 2 ? ` +${labels.length - 2}` : "";
            desc = `${head}${more}`;
          }
        }
        if (!desc) {
          return <i style={{ color: "#999" }}>sin descripción</i>;
        }
        return (
          <span
            style={{
              color: "#aaa",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
              maxWidth: "100%",
            }}
            title={desc}
          >
            {desc}
          </span>
        );
      },
    },
    {
      key: "total", label: "P. total", width: 110, align: "right",
      render: (_, row) => {
        // F4-QA2 — fallback: si row.total falta o es 0, recalcular desde items.
        // Después del backfill F4-QA2 todas las WO tienen total correcto, pero
        // mantener el fallback como defensa para WOs futuras que algún flujo
        // raro pudiera dejar sin total.
        const calc = (row.items || []).reduce(
          (s, it) => s + ((it.pUnit || 0) * (it.qty || 1)),
          0
        );
        const total = row.total || calc;
        return (
          <span style={{ color: total > 0 ? "#2ecc71" : "#666", fontWeight: 500 }}>
            ${fmtN(total)}
          </span>
        );
      },
    },
    {
      key: "origin", label: "Origen", width: 75,
      render: (_, row) => {
        const o     = row.origin || null;
        const color = ORIGIN_COLOR[o] || "#666";
        if (!o) return <span style={{ color: "#999" }}>origen no registrado</span>;
        const label = o === "COT" && !row.quotationRef ? "Cotización legacy" : (ORIGIN_LABEL[o] || o);
        return <span style={{ color, fontSize: 10, fontWeight: 500 }}>{label}</span>;
      },
    },
    {
      // F5.4.2 — Si la WO tiene customerOrderRef, el N° OC es link a /customerOrders.
      key: "clientOC", label: "N° OC", width: 90,
      stopPropagation: true,
      render: (_, row) => {
        if (!row.clientOC) return <span style={{ color: "#999", fontSize: 10 }}>{motivoSinOC(row)}</span>;
        // F5.9 — customerOrderRef ahora viene populado; extraer _id
        const coId = row.customerOrderRef?._id || row.customerOrderRef;
        if (!coId) {
          return <span style={{ fontFamily: "monospace", color: "#ccc", fontSize: 11 }}>{row.clientOC}</span>;
        }
        return (
          <a
            href={`/customerOrders?openId=${coId}`}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              navigate(`/customerOrders?openId=${coId}`);
            }}
            style={{
              fontFamily: "monospace", color: "#3498db", fontSize: 11,
              fontWeight: 600, textDecoration: "none", cursor: "pointer",
            }}
            title={`Ir a OC ${row.clientOC}`}
          >
            {row.clientOC}
          </a>
        );
      },
    },
    {
      key: "status", label: "Estado", width: 110,
      render: (_, row) => <StatusBadge status={row.status} />,
    },
    ...(puedeOperar ? [{
      key: "quickActions", label: "Acción", width: 84,
      stopPropagation: true,
      render: (_, row) => {
        if (!["working", "ready", "done", "delivered"].includes(row.status)) {
          return null;
        }
        return (
          <span style={{ display: "inline-flex", gap: 4 }}>
            <button
              type="button"
              className="bini-btn-icon"
              aria-label={`Facturar OT ${row.number}`}
              title="Emitir factura electrónica"
              onClick={e => { e.stopPropagation(); abrirFacturar(row); }}
              style={{ minWidth: 30, minHeight: 30, padding: 4 }}
            >
              🧾
            </button>
            <button
              type="button"
              className="bini-btn-icon"
              aria-label={`Emitir guía para OT ${row.number}`}
              title="Emitir guía de despacho"
              onClick={e => { e.stopPropagation(); abrirGuia(row); }}
              style={{ minWidth: 30, minHeight: 30, padding: 4 }}
            >
              📦
            </button>
          </span>
        );
      },
    }] : []),
  ];

  // ── detalle expandido ─────────────────────────────────────────────────────

  const renderDetail = (data, helpers) => {
    const subtotal = (data.items || []).reduce(
      (s, it) => s + (it.pUnit || 0) * (it.qty || 1), 0
    );

    return (
      <div className="bini-expand">

        {/* header */}
        <div className="bini-expand-header">
          <div>
            <div className="bini-expand-title">OT N° {data.number}</div>
            <div className="bini-expand-sub">
              {clienteOT(data)} · {fmt(data.date || data.createdAt)}
              {/* F5.4.2 — Origen OC clickeable cuando hay customerOrderRef */}
              {data.customerOrderRef && data.clientOC && (
                <>
                  {" · Origen: "}
                  <a
                    href={`/customerOrders?openId=${data.customerOrderRef}`}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`/customerOrders?openId=${data.customerOrderRef}`);
                    }}
                    style={{
                      color: "#3498db", textDecoration: "none",
                      fontFamily: "monospace", cursor: "pointer",
                    }}
                    title="Ir a la OC origen"
                  >
                    OC N° {data.clientOC} →
                  </a>
                </>
              )}
              {!data.customerOrderRef && data.origin === "OC" && data.clientOC && ` · OC cliente: ${data.clientOC}`}
              {/* F5.6 D-T59b — link a la cotización origen cuando hay quotationRef */}
              {data.quotationRef && (
                <>
                  {" · Origen: "}
                  <a
                    href={`/quotations?openId=${data.quotationRef}`}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`/quotations?openId=${data.quotationRef}`);
                    }}
                    style={{
                      color: "#a78bfa", textDecoration: "none",
                      fontFamily: "monospace", cursor: "pointer",
                    }}
                    title="Ir a la cotización origen"
                  >
                    COT N° {data.number} →
                  </a>
                </>
              )}
              {data.origin === "COT" && !data.quotationRef && " · cotización legacy"}
              {data.origin === "orphan" && " · huérfana (OC eliminada)"}
            </div>
          </div>
          <div className="bini-expand-actions">
            {!helpers.isEditing && (
              <>
                {/* 28-ago (roles): editar/facturar/eliminar solo admin/operator.
                    El seller conserva "Más detalles" en modo consulta. */}
                {puedeOperar && (
                <button
                  className="bini-btn bini-btn-edit"
                  onClick={e => { e.stopPropagation(); helpers.startEdit(); }}
                >
                  ✎ Editar
                </button>
                )}
                {/* 28-ago — OT terminada (ready): acceso directo al flujo de
                    emisión real (el mismo que vive en "Más detalles"). */}
                {/* 01-sep (pedido Maxi): la facturación PARCIAL es la norma del negocio —
                    el botón estaba solo en "ready" y una OT fabricando no se podía
                    facturar sin ir a Más detalles. Ahora acompaña todo el ciclo vivo. */}
                {puedeOperar && ["working", "ready", "done", "delivered"].includes(data.status) && (
                  <button
                    onClick={e => { e.stopPropagation(); abrirFacturar(data); }}
                    style={{
                      background: "#0d2a15", color: "#2ecc71",
                      border: "1px solid #0d4020", borderRadius: 6,
                      padding: "5px 12px", fontSize: 11, cursor: "pointer",
                      fontWeight: 700,
                    }}
                    title="Emitir factura electrónica"
                  >
                    🧾 Facturar
                  </button>
                )}
                <button
                  className="bini-btn-icon"
                  onClick={e => { e.stopPropagation(); setDetailsModal({ ...data }); }}
                >
                  ⊕ Más detalles
                </button>
                {puedeOperar && (
                <button
                  className="bini-btn-icon danger"
                  onClick={e => { e.stopPropagation(); setDeleteTarget(data); }}
                >
                  Eliminar
                </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* campos: 3 columnas (className="grid" colapsa a 2/1 en móvil) */}
        <div className="grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div className="bini-field">
            <label>Cliente</label>
            {helpers.isEditing ? (
              <select
                className="bini-input"
                name="client"
                value={data.client?._id || data.client || ""}
                onChange={e => {
                  const c = clients.find(cl => cl._id === e.target.value);
                  helpers.updateDraft(d => ({ ...d, client: c || { _id: e.target.value } }));
                }}
              >
                <option value="">Sin cliente</option>
                {clients.map(c => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            ) : (
              <div className="val">{clienteOT(data)}</div>
            )}
          </div>

          <div className="bini-field">
            <label>Estado</label>
            {helpers.isEditing ? (
              <select
                className="bini-input"
                name="status"
                value={data.status || "pending"}
                onChange={e => helpers.updateDraft(d => ({ ...d, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <div className="val"><StatusBadge status={data.status} /></div>
            )}
          </div>

          <div className="bini-field">
            <label>N° OC cliente</label>
            {helpers.isEditing ? (
              <input
                className="bini-input bini-mono"
                name="clientOC"
                value={data.clientOC || ""}
                onChange={e => helpers.updateDraft(d => ({ ...d, clientOC: e.target.value }))}
                placeholder="Ej: 33933"
              />
            ) : (
              <div className="val bini-mono">{data.clientOC || motivoSinOC(data)}</div>
            )}
          </div>
        </div>

        <div className="bini-field" style={{ marginTop: 10 }}>
          <label>Descripción</label>
          {helpers.isEditing ? (
            <input
              className="bini-input"
              name="description"
              value={data.description || ""}
              onChange={e => helpers.updateDraft(d => ({ ...d, description: e.target.value }))}
              placeholder="Descripción libre del trabajo..."
            />
          ) : (
            <div className="val">{data.description || data.name || "sin descripción registrada"}</div>
          )}
        </div>

        {/* F5.5 — N° factura + N° guía de despacho directos en el expand
            (antes solo en modal "Más detalles"). El input "factura" es target
            del navigateTo de la alerta cot_facturado_sin_dte. */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
          <div className="bini-field">
            <label>N° Factura</label>
            {helpers.isEditing ? (
              <input
                className="bini-input bini-mono"
                name="factura"
                value={data.factura || ""}
                onChange={e => helpers.updateDraft(d => ({ ...d, factura: e.target.value }))}
                placeholder="Ej: 1812"
              />
            ) : (
              <div className="val bini-mono">{data.factura || "sin factura emitida"}</div>
            )}
          </div>
          <div className="bini-field">
            <label>N° Guía de despacho</label>
            {helpers.isEditing ? (
              <input
                className="bini-input bini-mono"
                name="guiaDespacho"
                value={data.guiaDespacho || ""}
                onChange={e => helpers.updateDraft(d => ({ ...d, guiaDespacho: e.target.value }))}
                placeholder="Ej: G-2345"
              />
            ) : (
              <div className="val bini-mono">{data.guiaDespacho || "sin guía emitida"}</div>
            )}
          </div>
        </div>

        {/* ítems */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", margin: "14px 0 6px",
        }}>
          <span className="bini-section-title" style={{ margin: 0 }}>
            Ítems / piezas ({data.items?.length || 0})
          </span>
          {helpers.isEditing && (
            <button
              className="bini-btn-add"
              onClick={e => {
                e.stopPropagation();
                helpers.updateDraft(d => ({
                  ...d,
                  items: [...(d.items || []), {
                    sku: "", desc: "", qty: 1, pUnit: 0, source: null, pieceId: null,
                  }],
                }));
              }}
            >
              + Agregar ítem
            </button>
          )}
        </div>

        <table className="bini-items-table">
          <colgroup>{[70, 140, null, 50, 90, 100].map((w, i) => (
            <col key={i} style={w ? { width: w } : undefined} />
          ))}{helpers.isEditing && <col style={{ width: 30 }} />}</colgroup>
          <thead>
            <tr>
              <th>N°</th>
              <th>SKU</th>
              <th>Descripción</th>
              <th style={{ textAlign: "right" }}>Cant</th>
              <th style={{ textAlign: "right" }}>P. unit</th>
              <th style={{ textAlign: "right" }}>Subtotal</th>
              {helpers.isEditing && <th />}
            </tr>
          </thead>
          <tbody>
            {(!data.items || data.items.length === 0) && (
              <tr>
                <td colSpan={helpers.isEditing ? 7 : 6}
                    style={{ textAlign: "center", color: "var(--bini-text-muted)", padding: 16 }}>
                  Sin ítems
                </td>
              </tr>
            )}
            {(data.items || []).map((it, idx) => {
              const sub = (it.pUnit || 0) * (it.qty || 1);

              if (helpers.isEditing) {
                return (
                  <tr key={idx} data-item-idx={idx}>
                    <td className="bini-mono" style={{ color: "#aaa", fontSize: 10 }}>
                      {formatItemSubId(data.number, idx)}
                    </td>
                    <td colSpan={2} style={{ padding: "4px 6px" }}>
                      <PieceLookup
                        name="sku"
                        value={it.sku || it.desc ? {
                          pieceId:     it.pieceId || null,
                          sku:         it.sku     || "",
                          description: it.desc    || "",
                          price:       it.pUnit   || 0,
                          source:      it.source  || null,
                          temporary:   !!it.temporary,
                          expiresAt:    it.expiresAt || null,
                        } : null}
                        onChange={updated => helpers.updateDraft(d => {
                          const items = [...(d.items || [])];
                          items[idx] = {
                            ...items[idx],
                            sku:     updated?.sku         || "",
                            desc:    updated?.description || "",
                            pUnit:   updated?.price       ?? items[idx].pUnit,
                            source:  updated?.source      || null,
                            pieceId: updated?.pieceId     || null,
                            temporary: !!updated?.temporary,
                            expiresAt: updated?.expiresAt || null,
                          };
                          return { ...d, items };
                        })}
                      />
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input
                        type="number" min={1}
                        name="qty"
                        value={it.qty || 1}
                        onFocus={e => e.target.select()}
                        onChange={e => helpers.updateDraft(d => {
                          const items = [...(d.items || [])];
                          items[idx] = { ...items[idx], qty: Number(e.target.value) };
                          return { ...d, items };
                        })}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input
                        type="number" min={0}
                        name="pUnit"
                        value={it.pUnit || 0}
                        onFocus={e => e.target.select()}
                        onChange={e => helpers.updateDraft(d => {
                          const items = [...(d.items || [])];
                          items[idx] = { ...items[idx], pUnit: Number(e.target.value) };
                          return { ...d, items };
                        })}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td className={`price ${!sub ? "zero" : ""}`}>
                      {sub ? `$${fmtN(sub)}` : "sin precio"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="bini-btn-icon danger"
                        style={{ padding: "2px 5px", fontSize: 11 }}
                        onClick={() => helpers.updateDraft(d => ({
                          ...d,
                          items: (d.items || []).filter((_, i) => i !== idx),
                        }))}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={idx}>
                  <td className="bini-mono" style={{ color: "#aaa", fontSize: 11 }}>
                    {formatItemSubId(data.number, idx)}
                  </td>
                  <td>
                    <span className="bini-mono" style={{ color: "#3498db", fontSize: 11 }}>
                      {it.sku || "sin SKU"}
                    </span>
                  </td>
                  <td className="desc">{it.desc || "sin descripción"}</td>
                  <td className="num">{it.qty || 1}</td>
                  <td className={`price ${!it.pUnit ? "zero" : ""}`}>
                    {it.pUnit ? `$${fmtN(it.pUnit)}` : (
                      <span
                        title={
                          it.pieceId
                            ? "Click para cargar precio en catálogo"
                            : "Item sin SKU. Crear pieza en catálogo o ver detalle en /pulido"
                        }
                        style={{
                          color: "#cbd5e1",
                          fontStyle: "italic",
                          cursor: "pointer",
                          borderBottom: "1px dashed #888",
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          if (it.sku) {
                            navigate(`/pieces?search=${encodeURIComponent(it.sku)}`);
                          } else if (it.pieceId) {
                            navigate(`/pieces?search=${it.pieceId}`);
                          } else if (puedeVerPulido) {
                            navigate("/pulido#items-pendientes");
                          } else {
                            // /pulido es solo-admin: al resto lo mandamos al catálogo
                            navigate("/pieces?search=");
                          }
                        }}
                      >
                        sin precio
                      </span>
                    )}
                    {it.pUnit > 0 && (
                      <div
                        title="Snapshot del catálogo al momento de ingresar la OT. No se actualiza si cambia el catálogo después."
                        style={{
                          fontSize: 9,
                          color: "var(--bini-text-muted, #666)",
                          fontStyle: "italic",
                          marginTop: 2,
                          fontWeight: 400,
                        }}
                      >
                        válido al {fmt(data.date || data.createdAt)}
                      </div>
                    )}
                  </td>
                  <td className={`price ${!sub ? "zero" : ""}`}>
                    {sub ? `$${fmtN(sub)}` : "sin precio"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="bini-totals">
          <div className="bini-totals-box">
            <div className="bini-totals-row total">
              <span>Total</span>
              <span className="v">${fmtN(subtotal)}</span>
            </div>
          </div>
        </div>

        {/* F5.8b — Paneles operativos solo para OTs activas (no terminales).
            Las cerradas/históricas ven solo el resumen + documentos read-only.
            Editar procesos/control dim/upload de docs corresponde solo cuando
            la OT está viva en producción. */}
        {(() => {
          const isActiveWO = !TERMINAL_STATUSES.includes(data.status);
          if (helpers.isEditing) return null;
          return (
            <>
              {isActiveWO ? (
                <>
                  {data.origin === "interno" && puedeOperar && (
                    <CerrarInternoBox workOrderId={data._id} pieces={data.pieces} onClosed={() => load()} />
                  )}
                  <ActivePanelsTabs workOrderId={data._id} verCostos={puedeVerCostos}
                    quotationId={data.quotationRef?._id || data.quotationRef || null} />
                </>
              ) : (
                <>
                  <div style={{
                    marginTop: 14, padding: "10px 14px",
                    background: "#161616", border: "1px solid #2e2e2e",
                    borderRadius: 6, fontSize: 11, color: "#cbd5e1",
                  }}>
                    📦 OT cerrada ({data.status === "delivered" ? "entregada" : data.status === "facturado" ? "facturada" : "cancelada"})
                    — procesos y control dimensional ya no se editan. Documentos disponibles abajo.
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: "#cbd5e1",
                      textTransform: "uppercase", letterSpacing: ".07em",
                      marginBottom: 8,
                    }}>
                      Documentos del trabajo
                    </div>
                    <DocumentAttacher
                      refField="workOrderRefs"
                      refId={data._id}
                      defaultType="planoTecnico"
                    />
                  </div>
                  {/* Costeo por OT (27-ago): en las cerradas es donde más
                      interesa saber cuánto costó vs cuánto se vendió.
                      28-ago: solo admin/accountant (endpoint 403 para el resto). */}
                  {puedeVerCostos && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: "#cbd5e1",
                      textTransform: "uppercase", letterSpacing: ".07em",
                      marginBottom: 8,
                    }}>
                      💰 Costos del trabajo
                    </div>
                    <CostosOT workOrderId={data._id} />
                  </div>
                  )}
                </>
              )}
            </>
          );
        })()}

        {data.origin === "orphan" && (
          <div className="bini-warning">
            <div className="t">⚠ OT huérfana</div>
            <div className="d">
              Esta OT perdió su OC de origen. Administrala manualmente.
            </div>
          </div>
        )}

        {helpers.isEditing && (
          <div className="bini-action-bar">
            <div className="bini-action-bar-l" />
            <div className="bini-action-bar-r">
              <button
                className="bini-btn bini-btn-cancel"
                onClick={e => { e.stopPropagation(); helpers.cancelEdit(); }}
              >
                Cancelar
              </button>
              <button
                className="bini-btn bini-btn-save"
                disabled={helpers.isSaving}
                onClick={e => { e.stopPropagation(); helpers.saveEdit(); }}
              >
                {helpers.isSaving ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div style={S.wrap}>

      {/* header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 18,
        flexWrap: "wrap", gap: 8, // móvil: título y botones en 2 líneas
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Trabajos / OTs</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => navigate("/quotations")}
            style={{
              background: "transparent",
              border: "1px solid #a78bfa",
              color: "#a78bfa",
              borderRadius: 6,
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Ver cotizaciones
          </button>
          {puedeOperar && (
          <button
            onClick={() => navigate("/workOrders/new")}
            style={{
              background: "#e30613", color: "#fff", border: "none",
              borderRadius: 6, padding: "7px 16px", fontSize: 12,
              fontWeight: 700, cursor: "pointer",
            }}
          >
            + Nueva OT manual
          </button>
          )}
        </div>
      </div>

      {/* F5.8 — chips de origen (filtro ortogonal a status, con conteos) */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        {ORIGIN_CHIPS.map(chip => {
          const isActive = originFilter === chip.value;
          const count = originCounts[chip.value] ?? 0;
          return (
            <button
              key={chip.value}
              onClick={() => setOriginFilter(chip.value)}
              style={{
                background: isActive ? `${chip.color}22` : "transparent",
                border: `1px solid ${isActive ? chip.color : "#2e2e2e"}`,
                color: isActive ? chip.color: "#cbd5e1",
                padding: "4px 10px",
                borderRadius: 12,
                fontSize: 10,
                fontWeight: isActive ? 700 : 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title={
                chip.value === "oc_cliente" ? "OTs con OC formal del cliente"
                : chip.value === "cotizacion" ? "OTs que vinieron de cotización aprobada (sin OC)"
                : chip.value === "manual" ? "OTs manuales del cliente (verbal / sin cotización)"
                : chip.value === "interno" ? "OTs internas de Binimeliz (mantenimiento, prototipos)"
                : "Todas las OTs"
              }
            >
              {chip.label} <span style={{ opacity: 0.7, marginLeft: 4 }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* D-WO-Fase1 — cards top clickables (toggle filter exclusivo) */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {STATUS_CARDS.map(card => {
          const count = tabCounts[card.value] ?? 0;
          const isActive = activeTab === card.value;
          return (
            <div
              key={card.value}
              onClick={() => setActiveTab(card.value)}
              title={card.title || ""}
              className="t-02 tap-card"
              style={{
                // Estándar 28-ago: tarjeta #232323 radio 12; el color semántico
                // queda en el número y —activa— en el borde. El filtro no cambia.
                flex: "1 1 0", minWidth: 130, padding: "12px 14px",
                background: isActive ? `${card.color}14` : "#232323",
                border: `1px solid ${isActive ? card.color : "#2e2e2e"}`,
                borderRadius: 12, cursor: "pointer",
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: isActive ? card.color : "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" }}>
                {card.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: card.color, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                {count}
              </div>
            </div>
          );
        })}
      </div>

      {/* filtros — className="grid" colapsa a 2/1 columnas en móvil (index.css) */}
      <div className="grid" style={{
        display: "grid", gridTemplateColumns: "1fr 200px",
        gap: 8, marginBottom: 14,
      }}>
        <input
          className="bini-input"
          autoComplete="off"
          placeholder="Buscar N° OT, SKU, cliente, descripción..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <select
          className="bini-input"
          value={clientFilter}
          onChange={e => setClientFilter(e.target.value)}
        >
          <option value="">Todos los clientes</option>
          {clients.map(c => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* tabla */}
      <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflow: "hidden", background: "#242424" }}>
        <ExpandableTable
          columns={columns}
          rows={pageRows}
          renderDetail={renderDetail}
          onSave={handleSave}
          emptyText={loading ? "Cargando…" : "Sin OTs en este filtro"}
          initialExpandedId={pulidoLink.openId}
          initialEditingId={pulidoLink.editingId}
        />
      </div>

      {/* paginación */}
      {totalPages > 1 && (
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginTop: 14, fontSize: 11, color: "#cbd5e1",
          flexWrap: "wrap", gap: 8, // móvil
        }}>
          <div>
            Mostrando {((page - 1) * PER_PAGE) + 1}–{Math.min(page * PER_PAGE, filtered.length)} de {filtered.length}
          </div>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <button
              className="bini-btn-icon"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ opacity: page === 1 ? 0.4 : 1 }}
            >
              ◂
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .map((p, i, arr) => (
                <React.Fragment key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && (
                    <span style={{ color: "#999", padding: "0 4px" }}>…</span>
                  )}
                  <button
                    className="bini-btn-icon"
                    onClick={() => setPage(p)}
                    style={{
                      background: p === page ? "#3498db" : "#2e2e2e",
                      color: p === page ? "#fff" : "#aaa",
                      minWidth: 28,
                    }}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
            <button
              className="bini-btn-icon"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{ opacity: page === totalPages ? 0.4 : 1 }}
            >
              ▸
            </button>
          </div>
        </div>
      )}

      {/* modal "Más detalles" */}
      <ConfirmModal
        open={!!detailsModal}
        title={`Detalles administrativos — OT ${detailsModal?.number || ""}`}
        description={detailsModal ? clienteOT(detailsModal) : "cliente no disponible"}
        confirmLabel="Guardar"
        onConfirm={handleSaveDetails}
        onCancel={() => setDetailsModal(null)}
      >
        {detailsModal && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div className="bini-field">
                <label>N° Guía de despacho</label>
                <input
                  className="bini-input bini-mono"
                  value={detailsModal.guiaDespacho || ""}
                  onChange={e => setDetailsModal({ ...detailsModal, guiaDespacho: e.target.value })}
                  placeholder="Ej: G-2345"
                />
              </div>
              <div className="bini-field">
                <label>N° Factura</label>
                <input
                  className="bini-input bini-mono"
                  value={detailsModal.factura || ""}
                  onChange={e => setDetailsModal({ ...detailsModal, factura: e.target.value })}
                  placeholder="Ej: 1812"
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center", flexWrap: "wrap" /* móvil */ }}>
              <span style={{ fontSize: 11, color: "#999" }}>Pre-emisión (diseño Enternet):</span>
              <button type="button" className="bini-btn" onClick={() => handlePreDte("factura")}
                title="Genera la factura en formato Enternet con los ítems de la OT. Usa el N° Factura digitado arriba (o sugiere el siguiente folio). Sale marcada PRE-EMISIÓN.">
                📄 Factura PDF
              </button>
              <button type="button" className="bini-btn" onClick={() => handlePreDte("guia")}
                title="Genera la guía de despacho en formato Enternet con lo pendiente de entrega. Usa el N° Guía digitado arriba.">
                📄 Guía PDF
              </button>
              {/* Fase G (26-ago): emisión REAL vía SimpleFactura — por ítem con saldos.
                  En ambiente de pruebas sale marcada SIN VALIDEZ y no toca datos.
                  28-ago (roles): solo admin/operator emiten. */}
              {puedeOperar && (
              <button type="button" onClick={() => abrirFacturar()}
                style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020",
                  borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 700 }}
                title="Emitir factura electrónica">
                🧾 Facturar
              </button>
              )}
              {/* 27-ago: guía de despacho electrónica (DTE 52) real — mismo modal,
                  misma selección de ítems; NO descuenta saldos por facturar. */}
              {puedeOperar && (
              <button type="button" onClick={() => abrirGuia()}
                style={{ background: "#0d2035", color: "#3498db", border: "1px solid #1a4a6e",
                  borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 700 }}
                title="Abre Emitir con esta OT precargada: la guía electrónica (52) pide chofer, cédula, transportista y destino (Res. SII 154).">
                📦 Emitir Guía
              </button>
              )}
            </div>
            <div className="bini-field">
              <label>Observaciones</label>
              <textarea
                className="bini-input"
                rows={3}
                value={detailsModal.observations || ""}
                onChange={e => setDetailsModal({ ...detailsModal, observations: e.target.value })}
                style={{ resize: "vertical", minHeight: 60 }}
                placeholder="Notas internas, avisos, instrucciones..."
              />
            </div>
            <DocumentAttacher
              refField="workOrderRefs"
              refId={detailsModal._id}
              defaultType="facturaEmitida"
            />
          </>
        )}
      </ConfirmModal>

      {/* modal eliminar */}
      <ConfirmModal
        open={!!deleteTarget}
        title={`Eliminar OT N° ${deleteTarget?.number || ""}`}
        description={`Cliente: ${deleteTarget ? clienteOT(deleteTarget) : "no disponible"} · Total: $${fmtN(deleteTarget?.total)}`}
        warning={{
          title: "Acción irreversible",
          description: "La OT se eliminará permanentemente. La OC asociada (si existe) no se toca.",
        }}
        confirmLabel="Eliminar OT"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ── Modal único de facturación (fuente OT) ── */}
      {facturarTarget && (
        <FacturarModal
          fuente="ot"
          target={facturarTarget}
          onClose={() => setFacturarTarget(null)}
          onSaved={() => { load(); }}
        />
      )}

    </div>
  );
};

export default WorkOrders;
